import { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { Candy, Search, Save, FileSpreadsheet, FileText, Plus, Minus, Trash2, FolderOpen, Loader2, Star, Store, Ban, CheckCircle2, RefreshCw } from "lucide-react";

const API_BASE = `${process.env.REACT_APP_BACKEND_URL}/api`;
const token = () => localStorage.getItem("cf_token") || "";
const imageUrl = (p) => `${API_BASE}/slik/products/image/${p}?auth=${encodeURIComponent(token())}`;
const STORES = [{ v: "", l: "Ingen" }, { v: "falster", l: "Falster" }, { v: "ringsted", l: "Ringsted" }, { v: "begge", l: "Begge" }];

export function SlikBestilling() {
  const [products, setProducts] = useState([]);
  const [orders, setOrders] = useState([]);
  const [qty, setQty] = useState({});
  const [name, setName] = useState("");
  const [store, setStore] = useState("");
  const [currentId, setCurrentId] = useState(null);
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [syncing, setSyncing] = useState(false);

  const loadProducts = async () => {
    try { const { data } = await api.get("/slik/products"); setProducts(data); }
    catch { toast.error("Kunne ikke hente slik-katalog"); }
    finally { setLoading(false); }
  };
  const loadOrders = async () => {
    try { const { data } = await api.get("/slik/orders"); setOrders(data); } catch { /* */ }
  };
  useEffect(() => { loadProducts(); loadOrders(); }, []);

  const syncCatalog = async () => {
    if (!window.confirm("Opdatér slik-kataloget med nyeste varer? Dette tilføjer nye varianter og bevarer dine udsolgt-markeringer og bestillinger.")) return;
    setSyncing(true);
    try {
      const { data } = await api.post("/admin/seed-slik");
      await loadProducts();
      toast.success(`Katalog opdateret · ${data.seeded} varer`);
    } catch { toast.error("Kunne ikke opdatere kataloget"); }
    finally { setSyncing(false); }
  };

  const setQ = (nr, v) => setQty((q) => { const n = { ...q }; const num = Math.max(0, parseInt(v || 0, 10) || 0); if (num) n[nr] = num; else delete n[nr]; return n; });
  const bump = (nr, d) => setQty((q) => { const cur = q[nr] || 0; const num = Math.max(0, cur + d); const n = { ...q }; if (num) n[nr] = num; else delete n[nr]; return n; });

  const toggleSoldout = async (p) => {
    const next = !p.soldout;
    setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, soldout: next } : x)));
    if (next) setQty((q) => { const n = { ...q }; delete n[p.varenr]; return n; });
    try { await api.put(`/slik/products/${p.id}/soldout`, { soldout: next }); toast.success(next ? `${p.name} markeret udsolgt` : `${p.name} igen på lager`); }
    catch { toast.error("Kunne ikke opdatere"); setProducts((ps) => ps.map((x) => (x.id === p.id ? { ...x, soldout: p.soldout } : x))); }
  };

  // Antal gange hver vare optræder på tværs af gemte bestillinger
  const freq = useMemo(() => {
    const f = {};
    orders.forEach((o) => Object.keys(o.lines || {}).forEach((nr) => { f[nr] = (f[nr] || 0) + 1; }));
    return f;
  }, [orders]);

  const filtered = useMemo(() => {
    const s = search.trim().toLowerCase();
    const base = s ? products.filter((p) => p.name.toLowerCase().includes(s) || p.varenr.toLowerCase().includes(s)) : products;
    // Udsolgte nederst, derefter favoritter (oftest bestilte) øverst, ellers original rækkefølge
    return [...base].sort((a, b) => {
      const so = (a.soldout ? 1 : 0) - (b.soldout ? 1 : 0);
      if (so !== 0) return so;
      const fb = (freq[b.varenr] || 0) - (freq[a.varenr] || 0);
      if (fb !== 0) return fb;
      return (a.order || 0) - (b.order || 0);
    });
  }, [products, search, freq]);

  const lineCount = Object.keys(qty).length;
  const unitTotal = Object.values(qty).reduce((a, b) => a + b, 0);

  const save = async () => {
    if (lineCount === 0) { toast.error("Vælg mindst én vare"); return null; }
    setSaving(true);
    try {
      const body = { name, lines: qty, store };
      let id = currentId;
      if (currentId) await api.put(`/slik/orders/${currentId}`, body);
      else { const { data } = await api.post("/slik/orders", body); id = data.id; setCurrentId(id); setName(data.name); }
      await loadOrders();
      toast.success("Bestilling gemt");
      return id;
    } catch { toast.error("Kunne ikke gemme"); return null; }
    finally { setSaving(false); }
  };

  const exportFile = async (type) => {
    const id = await save();
    if (!id) return;
    window.open(`${API_BASE}/slik/orders/${id}/export.${type}?auth=${encodeURIComponent(token())}`, "_blank");
  };

  const loadOrder = (o) => { setCurrentId(o.id); setName(o.name); setStore(o.store || ""); const q = {}; Object.entries(o.lines || {}).forEach(([k, v]) => { q[k] = Math.round(v); }); setQty(q); toast.success(`Åbnede "${o.name}"`); };
  const newOrder = () => { setCurrentId(null); setName(""); setStore(""); setQty({}); };
  const delOrder = async (o) => { if (!window.confirm(`Slet "${o.name}"?`)) return; try { await api.delete(`/slik/orders/${o.id}`); if (currentId === o.id) newOrder(); loadOrders(); toast.success("Slettet"); } catch { toast.error("Kunne ikke slette"); } };

  return (
    <div className="space-y-5" data-testid="slik-bestilling">
      <div className="flex flex-col lg:flex-row lg:items-end justify-between gap-3">
        <div>
          <h4 className="font-heading text-xl font-light text-slate-900 flex items-center gap-2"><Candy className="w-5 h-5 text-pink-500" /> Slik bestilling</h4>
          <p className="text-sm text-slate-500">Sæt antal pr. vare og hent bestillingen som Excel eller PDF.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <Input value={name} onChange={(e) => setName(e.target.value)} placeholder="Navn på bestilling (fx Uge 42)" className="w-full sm:w-56" data-testid="slik-order-name" />
          <div className="inline-flex items-center rounded-lg border border-[#e2e8f0] overflow-hidden" data-testid="slik-store-select">
            <Store className="w-4 h-4 text-slate-400 ml-2.5 mr-1" />
            {STORES.map((s) => (
              <button key={s.v} onClick={() => setStore(s.v)} data-testid={`slik-store-${s.v || "none"}`}
                className={`px-2.5 py-1.5 text-sm transition-colors ${store === s.v ? "bg-slate-800 text-white" : "text-slate-600 hover:bg-slate-50"}`}>{s.l}</button>
            ))}
          </div>
          <Button onClick={save} disabled={saving} className="rounded-lg bg-slate-800 text-white hover:bg-slate-900 gap-1.5" data-testid="slik-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}Gem</Button>
          <Button onClick={() => exportFile("xlsx")} variant="outline" className="rounded-lg gap-1.5" data-testid="slik-export-xlsx"><FileSpreadsheet className="w-4 h-4 text-emerald-600" />Excel</Button>
          <Button onClick={() => exportFile("pdf")} variant="outline" className="rounded-lg gap-1.5" data-testid="slik-export-pdf"><FileText className="w-4 h-4 text-rose-600" />PDF</Button>
          <Button onClick={newOrder} variant="ghost" className="rounded-lg gap-1.5 text-slate-500" data-testid="slik-new-btn"><Plus className="w-4 h-4" />Ny</Button>
          <Button onClick={syncCatalog} disabled={syncing} variant="outline" className="rounded-lg gap-1.5 text-slate-600" data-testid="slik-sync-btn" title="Hent nyeste varer ind i kataloget">{syncing ? <Loader2 className="w-4 h-4 animate-spin" /> : <RefreshCw className="w-4 h-4" />}Opdatér katalog</Button>
        </div>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <div className="relative flex-1 min-w-[200px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <Input value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Søg vare eller varenr…" className="pl-9" data-testid="slik-search" />
        </div>
        <div className="text-sm text-slate-600" data-testid="slik-summary"><b>{lineCount}</b> varer · <b>{unitTotal}</b> kg valgt</div>
      </div>

      {orders.length > 0 && (
        <div className="flex flex-wrap items-center gap-2" data-testid="slik-saved-orders">
          <span className="text-xs text-slate-400 inline-flex items-center gap-1"><FolderOpen className="w-3.5 h-3.5" />Gemte:</span>
          {orders.map((o) => (
            <span key={o.id} className={`inline-flex items-center gap-1 pl-2.5 pr-1 py-1 rounded-full text-xs font-medium border ${currentId === o.id ? "bg-slate-800 text-white border-slate-800" : "bg-white border-[#e2e8f0] text-slate-600"}`}>
              <button onClick={() => loadOrder(o)} data-testid={`slik-open-${o.id}`}>{o.name}</button>
              <button onClick={() => delOrder(o)} className="opacity-60 hover:opacity-100"><Trash2 className="w-3 h-3" /></button>
            </span>
          ))}
        </div>
      )}

      {loading ? <p className="text-sm text-slate-500 py-6" data-testid="slik-loading">Henter katalog…</p> : (
        <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 xl:grid-cols-6 2xl:grid-cols-7 gap-3" data-testid="slik-grid">
          {filtered.map((p) => {
            const q = qty[p.varenr] || 0;
            return (
              <div key={p.id} className={`rounded-xl border p-2.5 bg-white transition-colors ${p.soldout ? "border-slate-200 opacity-70" : q > 0 ? "border-pink-400 ring-1 ring-pink-200" : "border-[#e2e8f0]"}`} data-testid={`slik-card-${p.varenr}`}>
                <div className="aspect-square rounded-lg bg-[#f8fafc] overflow-hidden flex items-center justify-center mb-2 relative">
                  {freq[p.varenr] > 0 && !p.soldout && <span className="absolute top-1 left-1 inline-flex items-center gap-0.5 bg-amber-400 text-white text-[10px] font-semibold px-1.5 py-0.5 rounded-full shadow-sm" data-testid={`slik-fav-${p.varenr}`}><Star className="w-2.5 h-2.5 fill-white" />{freq[p.varenr]}</span>}
                  <button onClick={() => toggleSoldout(p)} title={p.soldout ? "Sæt på lager igen" : "Markér udsolgt"} data-testid={`slik-soldout-toggle-${p.varenr}`}
                    className={`absolute top-1 right-1 w-6 h-6 rounded-full flex items-center justify-center shadow-sm transition-colors ${p.soldout ? "bg-emerald-500 text-white hover:bg-emerald-600" : "bg-white/90 text-slate-400 hover:text-[#C8102E] border border-[#e2e8f0]"}`}>
                    {p.soldout ? <CheckCircle2 className="w-3.5 h-3.5" /> : <Ban className="w-3.5 h-3.5" />}
                  </button>
                  {p.image_path ? <img src={imageUrl(p.image_path)} alt={p.name} className={`w-full h-full object-cover ${p.soldout ? "grayscale" : ""}`} loading="lazy" /> : <Candy className="w-8 h-8 text-slate-300" />}
                  {p.soldout && <span className="absolute inset-x-0 bottom-0 bg-[#C8102E] text-white text-[11px] font-bold text-center py-0.5 uppercase tracking-wide" data-testid={`slik-soldout-badge-${p.varenr}`}>Udsolgt</span>}
                </div>
                <p className="text-sm font-medium text-slate-800 leading-tight line-clamp-2 min-h-[2.4em]">{p.name}</p>
                <p className="text-[11px] text-slate-400 mb-2">{p.varenr}{p.per_kolli ? ` · ${p.per_kolli} pr kolli` : ""}</p>
                {p.soldout ? (
                  <div className="h-8 flex items-center justify-center text-[11px] font-medium text-slate-400" data-testid={`slik-soldout-note-${p.varenr}`}>Ikke til bestilling</div>
                ) : (
                  <div className="flex items-center gap-1">
                    <button onClick={() => bump(p.varenr, -1)} className="w-7 h-8 rounded-md border border-[#e2e8f0] text-slate-500 hover:bg-slate-50 flex items-center justify-center" data-testid={`slik-minus-${p.varenr}`}><Minus className="w-3.5 h-3.5" /></button>
                    <Input value={q || ""} onChange={(e) => setQ(p.varenr, e.target.value)} inputMode="numeric" placeholder="0" className="h-8 text-center px-1" data-testid={`slik-qty-${p.varenr}`} />
                    <span className="text-[11px] text-slate-400 font-medium">kg</span>
                    <button onClick={() => bump(p.varenr, 1)} className="w-7 h-8 rounded-md border border-[#e2e8f0] text-slate-500 hover:bg-slate-50 flex items-center justify-center" data-testid={`slik-plus-${p.varenr}`}><Plus className="w-3.5 h-3.5" /></button>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}
